import React, { useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Stack from "react-bootstrap/Stack";
import InputGroup from "react-bootstrap/InputGroup";
import { ProductionFormStateWithFrontEndOnlyFields } from "./utilities";
import { CC2MasterSheetDataRow } from "../../workers/sheets";
import { PMGradingRow } from "./types";
import { Printer } from "react-bootstrap-icons";

interface ProductionFormProps {
  pmRow: PMGradingRow;
  formData: Partial<ProductionFormStateWithFrontEndOnlyFields>;
  updateFormField: (callback: (prev: any) => any) => void;
  engineers: string[];
  faultCodeOptions: string[];
  returnReasonOptions: string[];
  gradeOptions: string[];
  clientCodeOptions: string[];
  packagingDescriptionOptions: string[];
  disposalReasonOptions: string[];
  activeTab: string;
  onSaveChanges: () => Promise<boolean>;
  isLoading: boolean;
  currentCC2?: CC2MasterSheetDataRow;
  disabled?: boolean;
  hideImageUpload?: boolean;
  setShowPalletLabelPrintModal: (show: boolean) => void;
  hasImages: boolean; // Add this prop to track image state
  onImageStateChange: (hasImages: boolean) => void; // Add callback for image state changes
}

const numberOfBoxesLimit = 8;

/**
 * Component for PM Grading production form
 */
const ProductionForm: React.FC<ProductionFormProps> = ({
  pmRow,
  formData,
  updateFormField,
  engineers,
  faultCodeOptions,
  returnReasonOptions,
  clientCodeOptions,
  gradeOptions,
  packagingDescriptionOptions,
  disposalReasonOptions,
  activeTab,
  currentCC2,
  disabled = false,
  hideImageUpload = false,
  setShowPalletLabelPrintModal,
  hasImages = false,
  onImageStateChange,
}) => {
  // Handle boolean toggle
  const handleBooleanToggle = (
    field: keyof ProductionFormStateWithFrontEndOnlyFields
  ) => {
    updateFormField((prev: any) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Listen for postMessage from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from same origin for security
      if (event.origin !== window.location.origin) return;

      if (
        event.data.type === "IMAGE_COUNT_UPDATE" &&
        event.data.entityId === activeTab
      ) {
        onImageStateChange?.(event.data.hasImages);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [activeTab, onImageStateChange]);

  // Check if form is invalid for BC grade without images
  const isBCGradeWithoutImages = formData.grade === "BC" && !hasImages;

  return (
    <Form>
      {/* Engineer selection */}
      <Form.Group className="mb-4" controlId={`formEngineer-${activeTab}`}>
        <Form.Label>Engineer</Form.Label>
        <Form.Control
          as="select"
          required
          isInvalid={!formData.engineer}
          value={formData.engineer || ""}
          isValid={!!formData.engineer}
          disabled={disabled}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              engineer: e.target.value,
            }))
          }
        >
          <option value="">-- Select engineer --</option>
          {engineers.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Does product match link toggle */}
      <Form.Group className="mb-4" controlId={`formProductMatch-${activeTab}`}>
        <Form.Check
          type="switch"
          isInvalid={!formData.doesProductMatchLink}
          disabled={disabled}
          label="Does product match product data (on right)?"
          checked={formData.doesProductMatchLink || false}
          onChange={() => handleBooleanToggle("doesProductMatchLink")}
        />
      </Form.Group>

      {/* Suspected reason for return */}
      <Form.Group className="mb-4" controlId={`formReturnReason-${activeTab}`}>
        <Form.Label>Suspected Reason for Return</Form.Label>
        <Form.Control
          as="select"
          required
          disabled={disabled}
          value={formData.suspectedReasonForReturn || ""}
          // isInvalid={!formData.suspectedReasonForReturn}
          isValid={!!formData.suspectedReasonForReturn}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              suspectedReasonForReturn: e.target.value,
            }))
          }
        >
          <option value="">-- Select return reason --</option>
          {returnReasonOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Fault code */}
      <Form.Group className="mb-4" controlId={`formFaultCode-${activeTab}`}>
        <Form.Label>Assessment Fault Code</Form.Label>
        <Form.Control
          as="select"
          required
          disabled={disabled}
          value={formData.assessmentFaultCode || ""}
          isValid={!!formData.assessmentFaultCode}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              assessmentFaultCode: e.target.value,
            }))
          }
        >
          <option value="">-- Select fault code --</option>
          {faultCodeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Grade selection */}
      <Form.Group className="mb-4" controlId={`formGrade-${activeTab}`}>
        <Form.Label>Grade</Form.Label>
        <Form.Control
          as="select"
          required
          disabled={disabled}
          value={formData.grade || ""}
          isInvalid={!formData.grade}
          isValid={!!formData.grade}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              grade: e.target.value,
            }))
          }
        >
          <option value="">-- Select grade --</option>
          {gradeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Listing comments */}
      <Form.Group
        className="mb-2"
        controlId={`formListingComments-${activeTab}`}
      >
        <Form.Label>Listing Comments</Form.Label>
        <Form.Control
          as="textarea"
          placeholder="Enter any comments for the listing here..."
          isInvalid={formData.grade === "BC" && !formData.listingComments}
          disabled={disabled}
          value={formData.listingComments || ""}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              listingComments: e.target.value,
            }))
          }
          rows={4}
        />
        <Form.Label className="text-muted" style={{ fontSize: "small" }}>
          {formData.grade === "BC"
            ? "Please provide comments for the listing, this is required for BC grade."
            : ""}
        </Form.Label>
      </Form.Group>

      {/* Client code change */}
      <Form.Group className="mb-4" controlId={`formClientCode-${activeTab}`}>
        <Form.Label>Client Code</Form.Label>
        <Form.Control
          as="select"
          required
          disabled={disabled}
          value={formData.clientCode}
          // isInvalid={!formData.clientCode}
          isValid={!!formData.clientCode}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              clientCode: e.target.value,
            }))
          }
        >
          {clientCodeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
        {pmRow &&
          formData.clientCode &&
          formData.clientCode !== pmRow.manifest.clientCode && (
            <span className="text-muted mt-1" style={{ fontSize: "small" }}>
              ⚠️ Changed from original ({pmRow.manifest.clientCode}). Labels
              will use the new client prefix.
            </span>
          )}
      </Form.Group>

      {/* Product images */}
      {hideImageUpload ? null : (
        <Form.Group
          className="mb-4"
          controlId={`formProductImages-${activeTab}`}
        >
          <Form.Label>Product Images</Form.Label>
          {isBCGradeWithoutImages && (
            <Form.Text className="text-danger d-block mb-2">
              Images are required for BC grade items.
            </Form.Text>
          )}
          <iframe
            className="w-100"
            // Emulate the border style of other input fields with error state
            style={{
              height: "600px",
              border: isBCGradeWithoutImages
                ? "1px solid var(--bs-danger)"
                : "1px solid var(--bs-border-color)",
              borderRadius: "var(--bs-border-radius)",
            }}
            src={`/image-upload-tool?page=pm&embedView=true&hideNavbar=true&advancedFeatures=false&advancedFeaturesToggle=hide&pmOnly=true&entityId=${activeTab}`}
            title="Image Upload Tool"
          />
        </Form.Group>
      )}

      {/* Disposal section */}
      <Form.Group className="mb-4" controlId={`formDisposal-${activeTab}`}>
        <Form.Label>Disposal Status/Reason</Form.Label>
        <br />
        <ButtonGroup vertical className="w-100">
          <Button
            disabled={disabled}
            size="lg"
            variant={
              !formData.disposal && !formData.disposalReason
                ? "success"
                : "outline-success"
            }
            onClick={() =>
              updateFormField((prev: any) => ({
                ...prev,
                disposal: false,
                disposalReason: "",
              }))
            }
          >
            Not for Disposal
          </Button>
          {disposalReasonOptions.map((option) => (
            <Button
              key={option}
              disabled={disabled}
              size="lg"
              variant={
                formData.disposalReason === option ? "danger" : "outline-danger"
              }
              onClick={() =>
                updateFormField((prev: any) => ({
                  ...prev,
                  disposal: true,
                  disposalReason: option,
                }))
              }
            >
              {option}
            </Button>
          ))}
        </ButtonGroup>
      </Form.Group>

      <Form.Group className="mb-4" controlId={`formNumberOfBoxes-${activeTab}`}>
        <Form.Label>Number of Boxes</Form.Label>
        <br />
        <ButtonGroup>
          {Array.from({ length: numberOfBoxesLimit }, (_, i) => (
            <Button
              key={i}
              disabled={disabled}
              size="lg"
              style={{ minWidth: "60px" }}
              variant={
                formData.productionNumberOfBoxes === i + 1
                  ? "primary"
                  : "outline-primary"
              }
              onClick={() =>
                updateFormField((prev: any) => ({
                  ...prev,
                  productionNumberOfBoxes: i + 1,
                }))
              }
            >
              {i + 1}
            </Button>
          ))}
        </ButtonGroup>
        <br />
        {!formData.productionNumberOfBoxes ? (
          <Form.Text className="text-danger">
            Please select the number of boxes as packaged.
          </Form.Text>
        ) : null}
      </Form.Group>

      {/* Packaging description */}
      <Form.Group className="mb-4" controlId={`formPackagingDesc-${activeTab}`}>
        <Form.Label>Description of Packaging</Form.Label>
        <Form.Control
          as="select"
          value={formData.productionDescriptionOfPackaging || ""}
          disabled={disabled}
          isInvalid={!formData.productionDescriptionOfPackaging}
          isValid={!!formData.productionDescriptionOfPackaging}
          onChange={(e) =>
            updateFormField((prev: any) => ({
              ...prev,
              productionDescriptionOfPackaging: e.target.value,
            }))
          }
        >
          <option value="">-- Select packaging description --</option>
          {packagingDescriptionOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Additional fields for display only */}
      {pmRow?.production?.spareParts ? (
        <Form.Group className="mb-4" controlId={`formSpareParts-${activeTab}`}>
          <Form.Label>Spare Parts Info</Form.Label>
          <br />
          <b>{pmRow.production.spareParts}</b>
        </Form.Group>
      ) : null}
      <Stack direction="horizontal" gap={4} className="mb-4">
        {pmRow?.production?.postProductionExit ? (
          <Form.Group
            className="mb-4"
            controlId={`formPostProductionExit-${activeTab}`}
          >
            <Form.Label>Post Production Exit</Form.Label>
            <br />
            <b>{pmRow.production.postProductionExit}</b>
          </Form.Group>
        ) : null}

        {pmRow?.production?.packagingShippingService ? (
          <Form.Group
            className="mb-4"
            controlId={`formPackagingShippingService-${activeTab}`}
          >
            <Form.Label>Packaging Info</Form.Label>
            <br />
            <b>{pmRow.production.packagingShippingService}</b>
          </Form.Group>
        ) : null}

        {pmRow?.production?.outboundShippingService ? (
          <Form.Group
            className="mb-4"
            controlId={`formOutboundShippingService-${activeTab}`}
          >
            <Form.Label>Outbound Shipping Service</Form.Label>
            <br />
            <b>{pmRow.production.outboundShippingService}</b>
          </Form.Group>
        ) : null}
      </Stack>

      {/* Is shipping suitable toggle */}
      <Form.Group
        className="mb-4"
        controlId={`formShippingService-${activeTab}`}
      >
        <Form.Label>
          Is above Shipping data/shipping service Suitable?
        </Form.Label>
        <br />
        <ButtonGroup>
          <Button
            size="lg"
            disabled={disabled}
            variant={
              formData.isShippingSuitable ? "success" : "outline-success"
            }
            onClick={() =>
              updateFormField((prev: any) => ({
                ...prev,
                isShippingSuitable: true,
              }))
            }
          >
            Yes
          </Button>
          <Button
            size="lg"
            disabled={disabled}
            variant={
              formData.isShippingSuitable === false
                ? "danger"
                : "outline-danger"
            }
            onClick={() =>
              updateFormField((prev: any) => ({
                ...prev,
                isShippingSuitable: false,
              }))
            }
          >
            No
          </Button>
        </ButtonGroup>
      </Form.Group>
      {/* Pallet Number */}
      <Form.Group
        style={{ marginBottom: "10rem" }}
        controlId={`formRefurbPalletNumber-${activeTab}`}
      >
        <Form.Label>Pallet Number</Form.Label>
        <InputGroup>
          <Button
            onClick={() => {
              updateFormField((prev: any) => ({
                ...prev,
                refurbPalletNumber: "N/A",
              }));
            }}
            disabled={disabled || formData.refurbPalletNumber === "N/A"}
            variant="info"
          >
            Enter N/A
          </Button>
          <Form.Control
            type="text"
            placeholder="Enter pallet number"
            value={formData.refurbPalletNumber || ""}
            isValid={!!formData.refurbPalletNumber}
            isInvalid={!formData.refurbPalletNumber}
            disabled={disabled}
            onChange={(e) =>
              updateFormField((prev: any) => ({
                ...prev,
                refurbPalletNumber: e.target.value.trim(),
              }))
            }
          />
          <Button
            variant="info"
            onClick={() => {
              if (setShowPalletLabelPrintModal) {
                setShowPalletLabelPrintModal(true);
              }
            }}
          >
            <Printer className="me-2" size={20} />
            Print Pallet Label
          </Button>
        </InputGroup>
        <Form.Text className="text-muted">
          Please enter the pallet number for the refurbished product. If not
          needed, enter "N/A".
        </Form.Text>
      </Form.Group>
    </Form>
  );
};

export default ProductionForm;
